import { formatHubwayzApiError } from "../../services/hubwayzHotelsApi";

function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const HUBWAYZ_REQUEST_CURRENCIES = new Set(["USD", "EUR", "GBP", "ILS"]);

export const HUBWAYZ_PAYMENT_CURRENCY = "ILS";

/**
 * One currency only. A second code makes Mishor return the other currency.
 * Missing or unknown codes return null — never a silent USD or EUR default.
 */
export function hubwayzCurrenciesForSelection(currency) {
  const code = String(currency || "").trim().toUpperCase();
  if (!HUBWAYZ_REQUEST_CURRENCIES.has(code)) return null;
  return [code];
}

/** Selected search currency from the hotel URL, then the saved search snapshot. */
export function readHubwayzSelectedCurrency({
  snapshot = null,
  urlCurrency = null,
  searchPhrase = null,
} = {}) {
  const fromRequest = Array.isArray(snapshot?.searchRequest?.currencies)
    ? snapshot.searchRequest.currencies[0]
    : null;
  const candidates = [
    urlCurrency,
    snapshot?.searchPhrase?.currency,
    fromRequest,
    searchPhrase?.currency,
  ];
  for (const raw of candidates) {
    const list = hubwayzCurrenciesForSelection(raw);
    if (list) return list[0];
  }
  return null;
}

/** Stay copied from the search the user already ran. */
export function stayFromHubwayzSnapshot(snapshot) {
  const checkIn = snapshot?.checkInIso || snapshot?.searchPhrase?.checkIn;
  const checkOut = snapshot?.checkOutIso || snapshot?.searchPhrase?.checkOut;
  if (!checkIn || !checkOut) return null;
  const rooms = snapshot?.searchPhrase?.rooms;
  return {
    checkIn: String(checkIn).slice(0, 10),
    checkOut: String(checkOut).slice(0, 10),
    rooms:
      Array.isArray(rooms) && rooms.length
        ? rooms
        : [{ adults: 2, childAges: [] }],
  };
}

/**
 * Second search on the hotel page. Uses the selected currency only.
 */
export function resolveHubwayzHotelPageSearch({
  snapshot = null,
  urlCurrency = null,
  now,
} = {}) {
  const displayCurrency = readHubwayzSelectedCurrency({ snapshot, urlCurrency });
  const currencies = hubwayzCurrenciesForSelection(displayCurrency);
  const stay = stayFromHubwayzSnapshot(snapshot) || liveStayForHotelPage(now);
  return {
    ok: Boolean(currencies),
    displayCurrency,
    currencies,
    stay,
    paymentCurrency: HUBWAYZ_PAYMENT_CURRENCY,
  };
}

/**
 * One conversion from supplier currency into the currency the user asked to see.
 * Rates are ILS per 1 unit. Same currency is returned unchanged.
 */
export function convertHubwayzSupplierAmountToDisplay({
  amount,
  supplierCurrency,
  displayCurrency,
  currencyRate = null,
} = {}) {
  const supplierList = hubwayzCurrenciesForSelection(supplierCurrency);
  const displayList = hubwayzCurrenciesForSelection(displayCurrency);
  const n = Number(amount);
  if (!supplierList || !displayList || !Number.isFinite(n)) {
    return {
      ok: false,
      reason: "unrecognized_currency",
      displayAmount: null,
      displayCurrency: null,
      fxApplied: false,
    };
  }
  const supplier = supplierList[0];
  const display = displayList[0];
  if (supplier === display) {
    return {
      ok: true,
      displayAmount: n,
      displayCurrency: display,
      fxApplied: false,
      rateUsed: 1,
    };
  }
  const from = supplier === "ILS" ? 1 : Number(currencyRate?.[supplier]);
  const to = display === "ILS" ? 1 : Number(currencyRate?.[display]);
  if (!Number.isFinite(from) || from <= 0 || !Number.isFinite(to) || to <= 0) {
    return {
      ok: false,
      reason: "missing_fx_rate",
      displayAmount: null,
      displayCurrency: display,
      fxApplied: false,
    };
  }
  return {
    ok: true,
    displayAmount: (n * from) / to,
    displayCurrency: display,
    fxApplied: true,
    rateUsed: from / to,
  };
}

/**
 * Dates and occupancy for a hotel-page search that is not copied from a previous session.
 * Mid-March is used while it is still far enough ahead; otherwise 45 days out, 3 nights, 2 adults.
 */
export function liveStayForHotelPage(now = new Date()) {
  const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 45);
  const marchYear = now.getMonth() > 2 ? now.getFullYear() + 1 : now.getFullYear();
  const march = new Date(marchYear, 2, 14);
  const checkIn = march.getTime() > min.getTime() ? march : min;
  const checkOut = new Date(
    checkIn.getFullYear(),
    checkIn.getMonth(),
    checkIn.getDate() + 3
  );
  return {
    checkIn: formatIsoDate(checkIn),
    checkOut: formatIsoDate(checkOut),
    rooms: [{ adults: 2, childAges: [] }],
  };
}

/** Keep only offers whose rooms all belong to this hotel. */
export function filterMishorResultsToHotel(mishorData, hotelId) {
  const hid = String(hotelId || "").trim();
  const source = mishorData && typeof mishorData === "object" ? mishorData : {};
  const results = Array.isArray(source.results) ? source.results : [];
  const kept = hid
    ? results.filter((offer) => {
        const items = Array.isArray(offer?.items) ? offer.items : [];
        return (
          items.length > 0 &&
          items.every((item) => String(item?.hotelId) === hid)
        );
      })
    : [];
  const hotelsById = {};
  const staticHotel = source.hotelsById?.[hid];
  if (staticHotel) hotelsById[hid] = staticHotel;
  return {
    ...source,
    results: kept,
    hotelsById,
  };
}

export function supplierStatusFromSearchResponse(res) {
  return String(res?.status || res?.data?.status || "")
    .trim()
    .toLowerCase();
}

/** Empty Hubwayz payload is not a finished search while the supplier is still working. */
export function hubwayzEmptySearchStillWorking(status, resultCount) {
  return (
    String(status || "").trim().toLowerCase() === "working" &&
    Number(resultCount) === 0
  );
}

export function hubwayzSearchFailure(err) {
  const http = Number(err?.response?.status);
  const data = err?.response?.data;
  const auth =
    http === 401 ||
    http === 403 ||
    data?.authError === true;
  const statusCode = auth ? http || 401 : Number.isFinite(http) ? http : null;
  return {
    auth,
    http: statusCode,
    status: "error",
    message: auth
      ? `שגיאת אימות${statusCode ? ` (${statusCode})` : ""}. לא מוצגות תוצאות שמורות או מזיכרון הדפדפן.`
      : formatHubwayzApiError(err),
  };
}
